import React from 'react';
import moment from 'moment';
import ManagerDashboardActions from 'actions/ManagerDashboardActions';
import {toggleSortDirection} from 'components/manager-dashboard/Common';
import ManagerCoachingRow from './manager-coaching/ManagerCoachingRow';
import orderBy from 'lodash/orderBy';
import get from 'lodash/get';
import upperFirst from 'lodash/upperFirst';
import WidgetHeader from 'components/manager-dashboard/WidgetHeader';
import SortableTableHeader from 'components/pure/sortable-tables/SortableTableHeader';
import DoublySortableTableHeader from 'components/pure/sortable-tables/DoublySortableTableHeader';
import connectToStores from 'alt-utils/lib/connectToStores';
import ManagerDashboardStore from 'stores/ManagerDashboardStore';
import UserStore from 'stores/UserStore';
import './AdminDashCoachingActivity.styl';
import {browserHistory} from 'react-router';
import {addSubtractDayIntervalEndDateExclusive} from '@store/coaching-plans';
import {totalForActivityRow} from 'components/dashboard/admin/manager-coaching/ManagerCoachingRow';
import {Toggle} from 'material-ui';
import GenericTooltip from 'components/pure/tooltips/GenericTooltip';
import MetricTooltip from 'components/pure/tooltips/MetricTooltip';
import {
  NEUTRAL_MAX,
  NEUTRAL_MIN,
} from 'components/dashboard/admin/manager-coaching/CoachingEffectivenessScore';
import {rankData, trendFromRanks} from 'components/dashboard/coaching-effectiveness/common';
import Button from '@material-ui/core/Button';
import AdminNewAccount from 'components/dashboard/admin/AdminNewAccount';
import Loading from 'components/pure/utils/Loading';
import UserActions from 'actions/UserActions';
import sum from 'utils/sum';
import round from 'utils/round';
import {formatAsHours} from 'components/dashboard/admin/manager-coaching/ManagerSparklineTooltip';
import WebDataButton from 'components/utils/WebDataButton';
import WebDataRocksActions, {
  WebDataType,
  xlsxDateFormat,
} from '@store/web-data-rocks/WebDataRocksActions';
import {HISTORY_ACTIONS, shouldUpdate, TOOLTIP_DELAY} from 'components/StatsPage2.utils';
import {scrollToTop} from 'utils/scrollToTop';

const widgetId = 'new-coaching-activity';
const widgetConfig = {
  widgetId,
  widgetType: 'MANAGER_COACHING_ACTIVITY',
};

const directReportsToggleTooltip = (
  <div className="direct-reports-toggle-popover">
    When deselected, shows coaching performed for all reps. When selected, shows coaching performed
    only to direct reports.
  </div>
);

export function transformData(rankedActivitiesArray, showEffectivenessTag, users) {
  // This function has a test in components/dashboard/admin/AdminDashCoachingActivityTest.js,
  // if making modifications to this component and need test data, can copy getTestData function here
  // and uncomment the following line
  // rankedActivitiesArray = getTestData();
  let transformedActivities = rankedActivitiesArray
    // only return rows where our user is populated
    .filter(item => users.has(item.user))
    .map(item => {
      const meetingActivity = item.coachingTime.meetingActivity || {data: []};
      const reviewActivity = item.coachingTime.reviewActivity || {data: []};
      return {
        coachingEffectivenessScore: item.coachingEffectivenessScore,
        ...item.coachingTime,
        ...item.feedbackGiven,
        ...item.ranks,
        ...item.teamCoaching,
        manager: users.get(item.user),
        totalActivity: totalForActivityRow(meetingActivity, reviewActivity),
        meetingActivity,
        reviewActivity,
        effTagLabel: item.tag?.label,
        effTagWorstOrBest: item.tag?.isBest ? 'best' : 'worst',
        effTagOrder: item.tag?.importanceOrder,
      };
    })
    .sort((a, b) => b.totalActivity - a.totalActivity)
    .map((item, rank) => {
      return {
        ...item,
        distribution: item.teamSize > 0 ? (item.membersCoachedCount / item.teamSize) * 100 : 0,
        responded:
          item.totalRequests > 0
            ? Math.round((item.requestsRespondedTo / item.totalRequests) * 100)
            : 0,
        activityHours: rank,
        roundedAverage:
          isFinite(item.scoredAverage) && !isNaN(item.scoredAverage)
            ? Math.round(item.scoredAverage)
            : 0,
        activityRank: rankData(item.previousActivityRank, item.activityRank),
        activityTrend: trendFromRanks(item.previousActivityRank, item.activityRank),
        scoreRank: rankData(item.previousScorecardRank, item.scorecardRank),
        scoreTrend: trendFromRanks(item.previousScorecardRank, item.scorecardRank),
        feedbacksCount: item.scoredCount + item.commentsCount + item.highlightsCount,
        hoursListened: item.secondsListened / 3600.0,
        coachingTimeReceived:
          getDurationAsSeconds('meetingTimeReceived')(item) +
          getDurationAsSeconds('reviewTimeReceived')(item),
        // Normalize name so sorting works correctly
        manager: item.manager.update('firstName', upperFirst),
      };
    });
  return showEffectivenessTag
    ? orderBy(transformedActivities, ['effTagOrder'], ['asc']).slice(0, 5)
    : transformedActivities;
}

// returns a function that takes an activity and returns the duration in seconds of durationProperty
function getDurationAsSeconds(durationProperty) {
  function getDurationByProperty(activity) {
    return moment.duration(activity[durationProperty]).asSeconds();
  }
  return getDurationByProperty;
}

@connectToStores
export default class AdminDashCoachingActivity extends React.PureComponent {
  static getStores() {
    return [ManagerDashboardStore, UserStore];
  }

  static getPropsFromStores() {
    const store = ManagerDashboardStore.getState();
    const widgetInfo = store.widgets && store.widgets.get(widgetId);
    const widgetLoaded = !!widgetInfo;
    return {
      users: UserStore.getState().users,
      ...widgetInfo,
      ...widgetConfig,
      widgetLoaded,
    };
  }

  constructor(props) {
    super(props);
    this.state = {
      // in the showEffectivenessTag mode we don't support sorting the table
      sortColumn: props.showEffectivenessTag ? '' : 'totalActivity',
      sortDirection: 'desc',
      directReportsOnly: false,
    };
    this.managerNameClick = this.managerNameClick.bind(this);
    this.renderRow = this.renderRow.bind(this);
    this.toggleSort = this.toggleSort.bind(this);
    this.fullReport = this.fullReport.bind(this);
    this.shouldRenderRow = this.shouldRenderRow.bind(this);
  }

  componentDidMount() {
    UserActions.fetchManagersAndCoaches.defer();

    const state = this.props.location && this.props.location.state ? this.props.location.state : {};
    const {directReportsOnly = null, toTop = null} = state;
    if (directReportsOnly !== null && directReportsOnly !== this.state.directReportsOnly) {
      // componentDidUpdate will getWidget
      this.setState({directReportsOnly});
    } else {
      this.getWidget();
    }
    if (toTop) {
      scrollToTop();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const datesChanged = shouldUpdate(prevProps, this.props, false, true);
    const directReportsOnlyChanged = this.state.directReportsOnly !== prevState.directReportsOnly;
    if (datesChanged || directReportsOnlyChanged) {
      this.getWidget();
    }
  }

  getWebData = () => {
    const {startDate, endDate} = this.props;

    return this.getSortedActivities().map(d => {
      return {
        Email: d.manager.email,
        'Start Date': startDate.format(xlsxDateFormat),
        'End Date': endDate.format(xlsxDateFormat),
        'Coaching Effectiveness Score': round(d.coachingEffectivenessScore) + '%',
        'Review Activity Hours': formatAsHours(sum(d.reviewActivity.data.map(d => d.value))),
        'Meeting Activity Hours': formatAsHours(sum(d.meetingActivity.data.map(d => d.value))),
        'Team Distribution': `${round(d.distribution)}%`,
        '% Of Requests Responded To': `${round(d.responded)}%`,
        'Total Feedback': d.feedbacksCount,
        'Feedback Hours Listened': round(d.hoursListened),
        'Calls Scored': d.scoredCount,
        'Team Score % Change': d.scoreTrend.trend && `${d.scoreTrend.trend}%`,
        'Team Score Rank': d.scoreRank.rank,
        'Team Score Rank Change': d.scoreRank.delta,
        'Team Activity % Change': d.activityTrend.trend && `${d.activityTrend.trend}%`,
        'Team Activity Rank': d.activityRank.rank,
        'Team Activity Rank Change': d.activityRank.delta,
      };
    });
  };

  getWidget() {
    const {adminId, startDate, endDate} = this.props;
    const {directReportsOnly} = this.state;

    // maxDate isn't needed when subtracting.
    const {startDate: previousStartDate, endDate: previousEndDate} =
      addSubtractDayIntervalEndDateExclusive('subtract', startDate, endDate.clone().startOf('day'));

    ManagerDashboardActions.getWidget.defer(
      {
        ...widgetConfig,
        directReportsOnly,
        startDate,
        endDate,
        previousStartDate,
        previousEndDate,
      },
      adminId
    );
  }

  managerNameClick(user) {
    const managerId = user.userId;
    const userInUserStore = this.props.users.find(u => u.userId === user.id);
    const roleToUse = userInUserStore.isTeamManager ? 'manager' : 'coach';

    const fromPath = this.props.fromPath;
    const state = fromPath
      ? {
          pathname: fromPath,
          action: HISTORY_ACTIONS.PUSH,
        }
      : {};
    const link = {
      pathname: `/dashv2/admin/${this.props.adminId}/${roleToUse}/${managerId}`,
      state,
    };
    browserHistory.push(link);
  }

  fullReport() {
    const state = {
      directReportsOnly: this.state.directReportsOnly,
      toTop: true,
    };
    const link = {
      pathname: `/dashv2/admin/${this.props.adminId}/coaching-activity`,
      state,
    };
    browserHistory.push(link);
  }

  shouldRenderRow({manager, teamSize}) {
    const {directReportsOnly} = this.state;
    // if direct reports isn't toggled always show
    if (!directReportsOnly) return true;

    // otherwise only render rows where the coach has a non-empty team
    const isPureCoach = manager.isCoach && teamSize === 0;
    return !isPureCoach;
  }

  /**
   * render one line of Manager activity
   */
  renderRow(
    managerCoachingActivity,
    index,
    maxFeedbacksCount,
    maxHoursListened,
    showEffectivenessTag,
    startDate,
    endDate
  ) {
    if (showEffectivenessTag && !managerCoachingActivity.effTagLabel) {
      return null;
    }

    const dateRangeUnder1Week = endDate.clone().subtract(1, 'week').isBefore(startDate);

    return (
      <ManagerCoachingRow
        key={index}
        directReportsOnly={this.state.directReportsOnly}
        dateRangeTooSmall={dateRangeUnder1Week}
        fromPath={this.props.fromPath}
        adminId={this.props.adminId}
        managerCoachingActivity={managerCoachingActivity}
        managerNameClick={this.managerNameClick}
        maxFeedbacksCount={maxFeedbacksCount}
        maxHoursListened={maxHoursListened}
        showEffectivenessTag={showEffectivenessTag}
        startDate={startDate}
        endDate={endDate}
      />
    );
  }

  toggleSort(column, changeDirection = null) {
    if (this.state.sortColumn !== column) {
      this.setState({
        sortColumn: column,
        sortDirection:
          changeDirection === null
            ? 'desc'
            : changeDirection
            ? this.state.sortDirection === 'desc'
              ? 'asc'
              : 'desc'
            : this.state.sortDirection,
      });
    } else {
      this.setState({
        sortDirection: toggleSortDirection(this.state.sortDirection),
      });
    }
  }

  getSortedActivities() {
    const {users, coachingActivity = [], showEffectivenessTag} = this.props;
    const {sortColumn, sortDirection} = this.state;

    let rankedActivities = transformData(coachingActivity.slice(), showEffectivenessTag, users);
    if (!showEffectivenessTag)
      rankedActivities = sortActivities(rankedActivities, sortColumn, sortDirection);

    return rankedActivities.filter(this.shouldRenderRow);
  }

  webDataOnClick = () => {
    // Dialog needs to be visible before data is set, so we need to show, then defer the setData call.
    WebDataRocksActions.showDialog('Admin Coaching Activity');
    setTimeout(() => WebDataRocksActions.setData(WebDataType.COACHING_ACTIVITY, this.getWebData()));
  };

  render() {
    const headers = [
      {
        label: <div> Manager Effectiveness </div>,
        key: 'Manager Effectiveness',
        className: 'manager-effectiveness',
        renderColumn: 'coachingEffectivenessScore',
        popoverClassName: 'admin-coaching-effectiveness-tooltip',
        tooltip: (
          <MetricTooltip
            header="Coaching Effectiveness"
            tooltip={`View each manager's ability to create meaningful behavior change in their reps through coaching. Managers who were very effective at coaching their team will have a thumbs-up icon and values above ${NEUTRAL_MAX}%. Managers who were not effective at coaching will have a thumbs-down icon and values below ${NEUTRAL_MIN}%. Managers whose coaching neither hurt nor helped their team's performance will have an equal sign and values between ${NEUTRAL_MIN}% and ${NEUTRAL_MAX}%.`}
            link="https://support.mediafly.com/hc/en-us/articles/13500140426523-Executive-Dashboard#h_01GVNA3W04HXNT67DM802V2K5R"
          />
        ),
      },
      {
        label: 'Activity (Hours)',
        className: 'manager-activity',
        renderColumn: 'totalActivity',
        tooltip: (
          <MetricTooltip
            header="Coaching Activity"
            tooltip="Explore how much coaching happened this period, including how much time managers spent reviewing calls vs. in coaching meetings. Is coaching happening sporadically or consistently? "
            link="https://support.mediafly.com/hc/en-us/articles/13500140426523-Executive-Dashboard#h_01GVNA3W04HXNT67DM802V2K5R"
          />
        ),
      },
      {
        label: 'Team Distribution',
        className: 'manager-distribution',
        renderColumn: 'distribution',
        tooltip: (
          <MetricTooltip
            header="Coaching Distribution"
            tooltip="Are your managers coaching all of their reps or only some? This could be a good thing or bad thing depending upon the needs of each rep. Research shows that managers and coaches should focus on B-players and new reps in order to generate a bigger improvement in call quality relative to the amount of time they spend coaching."
            link="https://support.mediafly.com/hc/en-us/articles/13500140426523-Executive-Dashboard#h_01GVNA3W04HXNT67DM802V2K5R"
          />
        ),
      },
      {
        label: '% Of Requests Responded To',
        className: 'manager-responded',
        renderColumn: 'responded',
        tooltip: (
          <MetricTooltip
            header="% Requests Responded To"
            tooltip="Reps who do not get coached on calls on which they requested feedback tend to become frustrated with the coaching program. Are your managers responding to these requests for help? "
            link="https://support.mediafly.com/hc/en-us/articles/13500140426523-Executive-Dashboard#h_01GVNA3W04HXNT67DM802V2K5R"
          />
        ),
      },
      {
        label: <div> Feedback Per Hours Listened </div>,
        key: 'Feedback Per Hours Listened',
        className: 'feedbacks-hours-listened',
        renderColumn: 'feedbacksCount',
        tooltip: (
          <MetricTooltip
            header="Team Activity Trend/Rank"
            tooltip="See how much time managers have spent listening to calls compared to the amount of coaching activity they've completed. The best coaches complete ~10 coaching activities per hour of calls reviewed."
            link="https://support.mediafly.com/hc/en-us/articles/13500140426523-Executive-Dashboard#h_01GVNA3W04HXNT67DM802V2K5R"
          />
        ),
      },
      {
        label: <div>Team Score Trend / Rank</div>,
        labelSorted: (
          <div>
            Team Score <b>Trend</b> / Rank
          </div>
        ),
        label2Sorted: (
          <div>
            Team Score Trend / <b>Rank</b>
          </div>
        ),
        key: 'Team Score Trend / Rank',
        className: 'manager-score-trend-rank',
        popoverClassName: 'manager-score-trend-rank-tooltip',
        renderColumn: 'scoreTrend',
        renderColumn2: 'scoreRank',
        tooltip: (
          <MetricTooltip
            header="Team Score Trend/Rank"
            // XXX just the two former tooltips put together
            tooltip={
              <div>
                If there is considerable Meeting Time or Review Time and the Team Call Score Trend
                did not improve, it could indicate a gap in coaching effectiveness. There is a lot
                of coaching happening, but it's not working. Consider training managers and coaches
                on how to coach their reps so call scores improve.
                <br />
                Which of your managers' teams show the largest increase in call quality? Which team
                is dropping relative to other teams? Top sales leaders personally get involved with
                call coaching of laggard teams to do skip-level coaching with reps directly.
              </div>
            }
            // XXX this anchor is just above the other one for score rank, so it's not ideal but okay
            link="https://support.mediafly.com/hc/en-us/articles/13500140426523-Executive-Dashboard#h_01GVNA3W04HXNT67DM802V2K5R"
          />
        ),
      },
      {
        label: 'Calls Scored',
        className: 'manager-score-count',
        renderColumn: 'scoredAverage',
        tooltip: (
          <MetricTooltip
            header="Calls Scored"
            tooltip="Coach recommends that managers and coaches score an average of two calls per rep per week for reps who need active call coaching. The color of the box represents whether the manager met that recommendation. Gray indicates an average of less than 0.5; yellow indicates an average between 0.5 and 1.5; green indicates an average greater than 1.5."
            link="https://support.mediafly.com/hc/en-us/articles/13500140426523-Executive-Dashboard#h_01GVNA3W04HXNT67DM802V2K5R"
          />
        ),
      },
      {
        label: <div>Team Activity Trend / Rank</div>,
        labelSorted: (
          <div>
            Team Activity <b>Trend</b> / Rank
          </div>
        ),
        label2Sorted: (
          <div>
            Team Activity Trend / <b>Rank</b>
          </div>
        ),
        key: 'Team Activity Trend / Rank',
        className: 'manager-activity-trend-rank',
        renderColumn: 'activityTrend',
        renderColumn2: 'activityRank',
        tooltip: (
          <MetricTooltip
            header="Team Activity Trend/Rank"
            // XXX just the two former tooltips put together
            tooltip={
              <div>
                Explore how teams are trending in call activity. Large fluctuations in this metric
                should be inspected further to ensure the team is focusing both on quantity of calls
                and quality of conversations.
                <br />
                See how each team stacks up on call activity. This metric is beneficial for
                recalibrating dial goals and target conversion rates across the organization.
              </div>
            }
            // XXX this anchor is just above the other one for activity rank, so it's not ideal but okay
            link="https://support.mediafly.com/hc/en-us/articles/13500140426523-Executive-Dashboard#h_01GVNA3W04HXNT67DM802V2K5R"
          />
        ),
      },
    ];
    const {showEffectivenessTag, startDate, endDate, isNewAccount, widgetLoaded} = this.props;
    const {sortColumn, sortDirection, directReportsOnly} = this.state;

    if (!widgetLoaded) return <Loading />;

    // in lieu of the activity table -
    // if we have no data (& asked to show labels), or we were passed isNewAccount
    // (see AdminSummaryDashboard) - show this placeholder instead
    if (widgetLoaded && isNewAccount) {
      return <AdminNewAccount />;
    }

    const sortedActivities = this.getSortedActivities();

    // add a max of a minimal number to avoid divide-by-zero
    const maxFeedbacksCount = Math.max(
      Math.max(...sortedActivities.map(it => it.feedbacksCount)),
      1
    );
    const maxHoursListened = Math.max(
      Math.max(...sortedActivities.map(it => it.hoursListened)),
      0.000001
    );

    const effTagWorstOrBest = showEffectivenessTag ? 'best-worst' : '';

    return (
      <div className={`widget admin-coaching-activity ${effTagWorstOrBest}`}>
        <WidgetHeader {...this.props} showDates title={'Coaching Activity'}>
          <WebDataButton onClick={this.webDataOnClick} tooltip="Export" />
          <GenericTooltip tooltip={directReportsToggleTooltip} positionTop delay={TOOLTIP_DELAY}>
            <Toggle
              className="activity-toggle"
              label="Direct reports only"
              labelPosition="right"
              toggled={directReportsOnly}
              onToggle={() =>
                this.setState({
                  directReportsOnly: !directReportsOnly,
                })
              }
            />
          </GenericTooltip>
        </WidgetHeader>
        <div className="manager-coaching-content">
          <div className="table-header">
            {headers.map((h, i) => (
              <div className={h.className} key={i}>
                {!h.renderColumn2 ? (
                  <SortableTableHeader
                    label={h.label}
                    sortDirection={sortDirection}
                    sortColumn={sortColumn}
                    renderColumn={h.renderColumn}
                    onClick={() => this.toggleSort(h.renderColumn)}
                    popoverMessage={h.tooltip}
                    popoverClassName={h.popoverClassName}
                    sortingDisabled={showEffectivenessTag}
                  />
                ) : (
                  <DoublySortableTableHeader
                    label={h.label}
                    labelSorted={h.labelSorted}
                    label2Sorted={h.label2Sorted}
                    sortDirection={sortDirection}
                    sortColumn={sortColumn}
                    renderColumn={h.renderColumn}
                    renderColumn2={h.renderColumn2}
                    onClick={this.toggleSort}
                    popoverMessage={h.tooltip}
                    popoverClassName={h.popoverClassName}
                    sortingDisabled={showEffectivenessTag}
                  />
                )}
              </div>
            ))}
          </div>
          {sortedActivities.length === 0 ? (
            <div className="empty-state" />
          ) : (
            sortedActivities.map((activity, i) =>
              this.renderRow(
                activity,
                i,
                maxFeedbacksCount,
                maxHoursListened,
                showEffectivenessTag,
                startDate,
                endDate
              )
            )
          )}
        </div>
        {showEffectivenessTag && (
          <div className="button-container full-report">
            <Button size="medium" variant="contained" color="primary" onClick={this.fullReport}>
              View Full Report
            </Button>
          </div>
        )}
      </div>
    );
  }

  componentWillUnmount() {
    ManagerDashboardActions.reset.defer();
  }
}

function coachLastOrderBy(collection, iteratees, orders) {
  return orderBy(
    collection,
    iteratees.map((iteratee, i) => {
      function coachLast(object) {
        if (object.isCoach) {
          // this ensures the coach's value will rank last no matter the sortDirection
          return orders[i] === 'asc' ? Number.MAX_VALUE : -1 * Number.MAX_VALUE;
        } else {
          return iteratee instanceof Function ? iteratee(object) : get(object, iteratee);
        }
      }
      return coachLast;
    }),
    orders
  );
}

function sortActivities(activities, sortColumn, sortDirection) {
  if (sortColumn === 'manager') {
    return orderBy(activities, ['manager.firstName'], [sortDirection === 'desc' ? 'asc' : 'desc']);
  }

  if (sortColumn === 'coachingEffectivenessScore') {
    return orderBy(activities, ['coachingEffectivenessScore'], [sortDirection]);
  }

  if (sortColumn === 'responded')
    return coachLastOrderBy(activities, ['responded', 'totalRequests'], [sortDirection, 'desc']);

  if (sortColumn === 'scoredAverage')
    return orderBy(activities, ['roundedAverage', 'scoredCount'], [sortDirection, sortDirection]);

  if (sortColumn === 'scoreTrend')
    return coachLastOrderBy(activities, ['scoreTrend.trend'], [sortDirection]);

  if (sortColumn === 'scoreRank')
    return coachLastOrderBy(
      activities,
      ['scoreRank.rank'],
      [sortDirection === 'desc' ? 'asc' : 'desc']
    );

  if (sortColumn === 'activityTrend')
    return coachLastOrderBy(activities, ['activityTrend.trend'], [sortDirection]);

  if (sortColumn === 'activityRank')
    return coachLastOrderBy(
      activities,
      ['activityRank.rank'],
      [sortDirection === 'desc' ? 'asc' : 'desc']
    );

  if (sortColumn === 'distribution')
    return coachLastOrderBy(activities, [sortColumn], [sortDirection]);

  return orderBy(activities, [sortColumn], [sortDirection]);
}
